import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  FileSearch,
  Search,
  Headphones,
  ClipboardList,
  FileText,
} from 'lucide-react';
import { UseCaseCard } from './UseCaseCard';
import { UseCaseModal } from './UseCaseModal';
import { useTheme } from './ThemeProvider';

const iconMap = {
  'file-search': FileSearch,
  'search': Search,
  'headphones': Headphones,
  'clipboard-list': ClipboardList,
  'file-text': FileText
};

export function UseCasesSection() {
  const { t } = useTranslation('home');
  const { theme } = useTheme();
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(null);

  const useCaseKeys = [
    'knowledgeSearch',
    'customerResponse',
    'documentIntelligence',
    'meetingFollowUp',
    'proposalReports'
  ] as const;

  const selectedCase = selectedUseCase
    ? {
        title: t(`useCases.cards.${selectedUseCase}.title`),
        long: t(`useCases.cards.${selectedUseCase}.long`),
        benefits: t(`useCases.cards.${selectedUseCase}.benefits`, { returnObjects: true }) as string[],
        icon: iconMap[t(`useCases.cards.${selectedUseCase}.icon`) as keyof typeof iconMap],
        color: t(`useCases.cards.${selectedUseCase}.color`) as 'blue' | 'red' | 'green' | 'purple' | 'orange' | 'teal' | 'indigo' | 'cyan'
      }
    : null;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1] as const
      }
    }
  };

  return (
    <section id="use-cases" className="relative py-20 overflow-hidden">
      {/* Semi-transparent scrim only — lets the global animated NeuralBackground
          (pulsing neural network + glow) show through for a consistent backdrop,
          while keeping enough contrast for the cards/text. */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-900/40 dark:to-gray-950/50"></div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, ${theme === 'dark' ? 'white' : 'black'} 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4"
          >
            {t('useCases.title')}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {t('useCases.subtitle').split('bunker.ai').map((part, index, array) => (
              <span key={index}>
                {part}
                {index < array.length - 1 && (
                  <motion.span
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="inline-block cursor-pointer relative px-2 py-1 rounded"
                    whileHover={{
                      scale: 1.1,
                    }}
                    whileTap={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent relative z-10">
                      bunker.ai
                    </span>
                    {/* Subtle Glow on Hover */}
                    <motion.span
                      className="absolute inset-0 rounded bg-gradient-to-r from-orange-400/20 to-red-400/20 blur-md"
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileHover={{
                        opacity: 1,
                        scale: 1.05,
                      }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.span>
                )}
              </span>
            ))}
          </motion.p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {useCaseKeys.map((key) => (
            <motion.div key={key} variants={itemVariants}>
              <UseCaseCard
                title={t(`useCases.cards.${key}.title`)}
                short={t(`useCases.cards.${key}.short`)}
                icon={iconMap[t(`useCases.cards.${key}.icon`) as keyof typeof iconMap]}
                color={t(`useCases.cards.${key}.color`) as 'blue' | 'red' | 'green' | 'purple' | 'orange' | 'teal' | 'indigo' | 'cyan'}
                onClick={() => setSelectedUseCase(key)}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Modal */}
      {selectedCase && (
        <UseCaseModal
          isOpen={!!selectedUseCase}
          onClose={() => setSelectedUseCase(null)}
          title={selectedCase.title}
          long={selectedCase.long}
          benefits={selectedCase.benefits}
          icon={selectedCase.icon}
          color={selectedCase.color}
        />
      )}
    </section>
  );
}
